import React from 'react';
import styled from 'styled-components';
import breakpoints from '@/constants/breakpoints';
import Leaf from '../../common/Leaf';
import Fabric from '../../common/Fabric';
import Flag from '../../common/Flag';
import Domino from '../../common/Domino';
import ElleDecor from '../../common/ElleDecor';
import NYT from '../../common/NYT';
import MyDomaine from '../../common/MyDomaine';
import HouseBeautiful from '../../common/HouseBeautiful';
import useMedia from '@/hooks/useMedia';

export default function StayInStyle() {
  const isMobile =
    typeof window !== `undefined` ? useMedia(`(max-width: ${breakpoints['tablet'] - 1}px)`) : false;

  const isTablet =
    typeof window !== `undefined` ? useMedia(`(max-width: ${breakpoints['desktop'] - 1}px)`) : false;

  const isDesktop =
    typeof window !== `undefined` ? useMedia(`(max-width: ${breakpoints['large-desktop'] - 1}px)`) : false;

  const featureCards = [
    {
      copy: 'Sustainably Made',
      icon: <Leaf width={isMobile ? 36 : isTablet ? 25.2 : isDesktop ? 31 : 41} />
    },
    {
      copy: 'Quality Fabrics',
      icon: <Fabric width={isMobile ? 41 : isTablet ? 37.6 : isDesktop ? 46 : 61} />
    },
    {
      copy: 'One-of-a-Kind Prints',
      icon: <Flag width={isMobile ? 36 : isTablet ? 31 : isDesktop ? 38 : 50} />
    }
  ];

  const mediaLogos = [
    <Domino width={isTablet ? 64 : isDesktop ? 99 : 129}/>,
    <ElleDecor width={isTablet ? 62 : isDesktop ? 94 : 116}/>,
    <NYT width={isTablet ? 109 : isDesktop ? 167 : 218}/>,
    <MyDomaine width={isTablet ? 85.5 : isDesktop ? 139 : 170.5}/>,
    <HouseBeautiful width={isTablet ? 62.5 : isDesktop ? 96 : 125}/>
  ];

  return (
    <Container>
      <div className="title-container">
        <Title>
          Furniture that makes a&nbsp;statement
        </Title>
      </div>
      <FeaturesContainer>
        {featureCards.map(({ copy, icon }) => (
          <FeatureCard key={copy}>
            {icon}
            <Copy>{copy}</Copy>
          </FeatureCard>
        ))}
      </FeaturesContainer>
      <FeaturedInContainer>
        <FeaturedInTitle />
        <MediaContainer>
          {mediaLogos.map((logo, index) => (
            <div key={index}>{logo}</div>
          ))}
        </MediaContainer>
      </FeaturedInContainer>
    </Container>
  );
}

function FeaturedInTitle() {
  return (
    <FeaturedInTitleContainer>
      <Line />
      <span>Featured In</span>
      <Line />
    </FeaturedInTitleContainer>
  );
}

const Container = styled.div`
  background-color: ${props => props.theme.colors.olive};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 0 68px;

  @media (min-width: ${breakpoints['tablet']}px) {
    padding: 38px;
  }

  @media (min-width: ${breakpoints['desktop']}px) {
    padding: 62px 121px;
  }

  @media (min-width: ${breakpoints['large-desktop']}px) {
    padding: 86px 135px;
  }
`;

const FeaturesContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 40px;

  @media (min-width: ${breakpoints['tablet']}px) {
    flex-direction: row;
    gap: 71.7px;
    margin-bottom: 41px;
    margin-left: 40px;
  }

  @media (min-width: ${breakpoints['desktop']}px) {
    gap: 100px;
    margin-left: 50px;
  }

  @media (min-width: ${breakpoints['desktop']}px) {
    gap: 130px;
  }
`;

const FeatureCard = styled.div`
  text-align: center;
`;

const Title = styled.h2`
  font-family: ${({ theme }) => theme.fonts.secondary.family};
  letter-spacing: ${({ theme }) => theme.old ? '-0.52px' : theme.fonts.secondary.letterSpacing};
  font-weight: 300;
  font-size: 26px;
  color: ${props => props.theme.colors.oatmilk};
  line-height: 1.23;
  text-align: center;
  margin-bottom: 39px;
  max-width: 280px;

  @media (min-width: ${breakpoints['tablet']}px) {
    font-family: ${({ theme }) => theme.fonts.secondary.family};
    letter-spacing: ${({ theme }) => theme.old ? '-0.56px' : theme.fonts.secondary.letterSpacing};
    font-size: 28px;
    font-weight: 300;
    line-height: 1.14;
    margin-bottom: 42px;
    max-width: unset;
  }

  @media (min-width: ${breakpoints['desktop']}px) {
    font-size: 40px;
    line-height: normal;
    letter-spacing: -0.8px;
    margin-bottom: 50px;
  }

  @media (min-width: ${breakpoints['large-desktop']}px) {
    font-size: 56px;
    line-height: 0.89;
    letter-spacing: -1.68px;
    margin-bottom: 77px;
  }
`;

const Copy = styled.p`
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: 16px;
  color: ${props => props.theme.colors.oatmilk};
  line-height: 1.5;
  text-align: center;
  margin-top: 14px;

  @media (min-width: ${breakpoints['desktop']}px) {
    font-size: 20px;
  }
`;

const FeaturedInContainer = styled.div`
  display: none;

  @media (min-width: ${breakpoints['tablet']}px) {
    display: block;
  }
`;

const FeaturedInTitleContainer = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  margin-bottom: 25px;

  span {
    color: ${props => props.theme.colors.oatmilk};
    font-family: ${({ theme }) => theme.fonts.primary};
    font-size: 14px;
    line-height: 1.11;
    padding: 0 17px;
  }
`;

const Line = styled.div`
  flex: 1;
  background-color: ${props => props.theme.colors.oatmilk};
  height: 0.5px;
`;

const MediaContainer = styled.div`
  display: flex;
  gap: 40px;
`;
